import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useToast } from '../context/ToastContext.js';
import './CoachPracticeAnalytics.css';

const APP_META = {
  vault: { label: 'Vault', emoji: '🐎', color: '#FF6B6B' },
  bars:  { label: 'Bars',  emoji: '➰', color: '#F7A440' },
  beam:  { label: 'Beam',  emoji: '🎋', color: '#14b8a6' },
  floor: { label: 'Floor', emoji: '🤸', color: '#8B5CF6' },
  general: { label: 'General', emoji: '⭐', color: '#667eea' },
};
const TYPE_LABEL = {
  drill: '🎯 Drill', skill: '💪 Skill', strength_conditioning: '⚡ S&C',
  flexibility: '🧘 Flexibility', conditioning: '🏃 Conditioning', cool_down: '🌙 Cool Down',
};
const appMeta = (a) => APP_META[a] || APP_META.general;
const iso = (d) => d.toISOString().slice(0, 10);

function CoachPracticeAnalytics({ embedded = false }) {
  const toast = useToast();
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');       // '' = all classes
  const [range, setRange] = useState(() => {
    const end = new Date();
    const start = new Date(); start.setDate(start.getDate() - 30);
    return { start: iso(start), end: iso(end) };
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`${API_BASE_URL}/api/classes?active_only=true`)
      .then(r => setClasses(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (classId) params.set('class_id', classId);
      if (range.start) params.set('start_date', range.start);
      if (range.end) params.set('end_date', range.end);
      const res = await api.get(`${API_BASE_URL}/api/v1/practice-plans/analytics?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error('Analytics load error:', err);
      toast?.error?.('Failed to load practice analytics');
    } finally {
      setLoading(false);
    }
  }, [classId, range, toast]);

  useEffect(() => { load(); }, [load]);

  const preset = (days) => {
    const end = new Date();
    const start = new Date(); start.setDate(start.getDate() - days);
    setRange({ start: iso(start), end: iso(end) });
  };

  const totals = data?.totals || {};
  const bySkill = data?.by_skill || [];
  const byApp = data?.by_apparatus || [];
  const byType = data?.by_type || [];
  const maxSkillReps = Math.max(1, ...bySkill.map(s => Number(s.total_reps) || 0));
  const maxAppReps = Math.max(1, ...byApp.map(a => Number(a.total_reps) || 0));
  const hasData = (Number(totals.activities) || 0) > 0;

  return (
    <div className="pa-page">
      {!embedded && (
        <div className="pa-header">
          <div>
            <h1 className="pa-title">Practice Analytics</h1>
            <p className="pa-subtitle">Training volume from your practice plans — reps, sets &amp; time by skill and apparatus</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="pa-filters">
        <select className="pa-select" value={classId} onChange={e => setClassId(e.target.value)}>
          <option value="">All classes</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
        </select>
        <div className="pa-dates">
          <input type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} />
          <span>→</span>
          <input type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} />
        </div>
        <div className="pa-presets">
          <button className="pa-preset" onClick={() => preset(7)}>7d</button>
          <button className="pa-preset" onClick={() => preset(30)}>30d</button>
          <button className="pa-preset" onClick={() => preset(90)}>90d</button>
        </div>
      </div>

      {loading ? (
        <div className="pa-loading"><div className="pa-spinner" />Loading analytics…</div>
      ) : !hasData ? (
        <div className="pa-empty">
          <div className="pa-empty-icon">📊</div>
          <h3>No practice data in this range</h3>
          <p>Log drills with sets &amp; reps in your practice plans, then come back to see the trends.</p>
        </div>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="pa-tiles">
            <div className="pa-tile">
              <span className="pa-tile-num">{totals.sessions || 0}</span>
              <span className="pa-tile-label">Practices</span>
            </div>
            <div className="pa-tile tile-blue">
              <span className="pa-tile-num">{Number(totals.total_reps || 0).toLocaleString()}</span>
              <span className="pa-tile-label">Total reps</span>
            </div>
            <div className="pa-tile tile-purple">
              <span className="pa-tile-num">{Math.round((totals.total_minutes || 0) / 60 * 10) / 10}</span>
              <span className="pa-tile-label">Training hours</span>
            </div>
            <div className="pa-tile tile-green">
              <span className="pa-tile-num">{totals.distinct_skills || 0}</span>
              <span className="pa-tile-label">Distinct skills</span>
            </div>
          </div>

          <div className="pa-grid">
            {/* Top skills by volume */}
            <div className="pa-card pa-card-wide">
              <h2 className="pa-card-title">Top skills by volume</h2>
              <table className="pa-table">
                <thead>
                  <tr><th>Skill</th><th>Apparatus</th><th className="num">Times</th><th className="num">Reps</th><th className="num">Min</th><th className="bar-col"></th></tr>
                </thead>
                <tbody>
                  {bySkill.map((s, i) => {
                    const m = appMeta(s.apparatus);
                    const pct = Math.round((Number(s.total_reps) || 0) / maxSkillReps * 100);
                    return (
                      <tr key={i}>
                        <td className="pa-skill-name">{s.skill}</td>
                        <td>{s.apparatus ? <>{m.emoji} {m.label}</> : '—'}</td>
                        <td className="num">{s.times}</td>
                        <td className="num"><strong>{Number(s.total_reps).toLocaleString()}</strong></td>
                        <td className="num">{s.minutes}</td>
                        <td className="bar-col">
                          <div className="pa-bar-track"><div className="pa-bar-fill" style={{ width: `${pct}%`, background: m.color }} /></div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* By apparatus */}
            <div className="pa-card">
              <h2 className="pa-card-title">Volume by apparatus</h2>
              <div className="pa-app-list">
                {byApp.map((a, i) => {
                  const m = appMeta(a.apparatus);
                  const pct = Math.round((Number(a.total_reps) || 0) / maxAppReps * 100);
                  return (
                    <div key={i} className="pa-app-row">
                      <div className="pa-app-head">
                        <span>{m.emoji} {m.label}</span>
                        <span className="pa-app-reps">{Number(a.total_reps).toLocaleString()} reps · {a.minutes}m</span>
                      </div>
                      <div className="pa-bar-track"><div className="pa-bar-fill" style={{ width: `${pct}%`, background: m.color }} /></div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* By type */}
            <div className="pa-card">
              <h2 className="pa-card-title">Time by activity type</h2>
              <div className="pa-type-list">
                {byType.map((t, i) => (
                  <div key={i} className="pa-type-row">
                    <span>{TYPE_LABEL[t.type] || t.type}</span>
                    <span className="pa-type-min">{t.minutes} min · {t.activities}×</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CoachPracticeAnalytics;
